import React from 'react'
import { Wrapper } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import { themeSelector, usersSelector } from 'src/state/selectors'
import Button from 'src/global/Button'
import { isPaying, getDaysLeft, UserPatchType } from '@meniudigital/shared'
import { useLocation, useNavigate } from 'react-router-dom'
import { State } from 'src/state'
import { Screen } from 'src/screens'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import { userEndpoints } from 'src/api'
import { IS_DESKTOP_OR_TABLET } from '../HomeScreen'
import { patchCurrentUser, getCurrentUser } from 'src/state/users'
import SubscriptionInfoModal from './SubscriptionInfoModal'

export default function MySubscriptionScreen() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector(usersSelector)
  const { isDark } = useSelector(themeSelector)
  const { list: businessOrderList } = useSelector((state: State) => state.businessOrders)
  const location = useLocation()
  const [isInfoModalOpen, setIsInfoModalOpen] = React.useState(false)

  // Opened straight onto the benefits when another screen sent the owner here for them (the retouch limit
  // dialog). The flag is then cleared from the history entry, so a reload or a step back does not reopen them
  React.useEffect(() => {
    if ((location.state as { openInfoModal?: boolean } | null)?.openInfoModal) {
      setIsInfoModalOpen(true)
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.state])

  React.useEffect(() => {
    if (!isPaying(user)) {
      try {
        const { fbq } = window as any
        fbq?.('track', 'Lead', {
          content_category: 'subscription_interest',
        })
      } catch (e) {}
    } else {
      dispatch(getCurrentUser())
    }
  }, [])

  const goToRenewSubscription = () => {
    setTimeout(() => {
      userEndpoints.registerUserAction({ actionType: 'HasClickedOnExtendBusiness', data: {} })
    }, 0)
    navigate(user?.subscriptionExpiresAt ? Screen.RenewSubscription : Screen.ChooseSubscription)
  }

  const cancelRecurringPayment = async () => {
    await window.showModal({
      title: 'Dezactivare plată recurentă',
      textComponent: (
        <span>
          Ești sigur că dorești să dezactivezi plata recurentă?
          <br />
          <br />
          Abonamentul tău va rămâne activ până la data expirării ({moment(user?.subscriptionExpiresAt).format('DD MMMM YYYY')}),
          dar nu va fi reînnoit automat.
        </span>
      ),
      confirmButtonText: 'Da, dezactivează',
      cancelButtonText: 'Nu',
    })

    dispatch(
      patchCurrentUser({
        type: UserPatchType.RecurringPayment,
        data: {
          isActive: false,
          cancelReason: '',
        },
      })
    )
  }

  const reactivateRecurringPayment = async () => {
    await window.showModal({
      title: 'Reactivare plată recurentă',
      textComponent: (
        <span>
          Ești sigur că dorești să reactivezi plata recurentă?
          <br />
          <br />
          Abonamentul va fi reînnoit automat la data expirării ({moment(user?.subscriptionExpiresAt).format('DD MMMM YYYY')}).
        </span>
      ),
      confirmButtonText: 'Da, reactivează',
      cancelButtonText: 'Nu',
    })

    dispatch(
      patchCurrentUser({
        type: UserPatchType.RecurringPayment,
        data: {
          isActive: true,
        },
      })
    )
  }

  if (!user) {
    return null
  }

  return (
    <Wrapper>
      <div className={'page' + (isPaying(user) ? ' is-business' : '')}>
        <div className="section-wrapper">
          <label>Abonamentul meu</label>

          <div className="header">
            <div className="subscription-card">
              <img
                src={isDark || isPaying(user) ? '/logo-white.svg' : '/logo-grayer.svg'}
                alt="MeniuDigital Logo"
                className="logo-img"
              />
              <div>
                <div className="brand-name">Abonament</div>
                <div className="active-text">{isPaying(user) ? 'ACTIV' : 'INACTIV'}</div>
              </div>
            </div>
            <div className="price-and-button">
              <div className="subscription-price">
                99 lei/lună
                <br />
                <button type="button" className="benefits-button" onClick={() => setIsInfoModalOpen(true)}>
                  Ce include abonamentul?
                </button>
              </div>
              <div className="subscription-duration">
                {user?.subscriptionExpiresAt ? (
                  <>
                    <span
                      className={`next-bill-date ${getDaysLeft(user?.subscriptionExpiresAt) < 60 ? 'red' : 'blue'}`}
                      style={{ fontWeight: 600 }}
                    >
                      {getDaysLeft(user?.subscriptionExpiresAt)} zile rămase
                    </span>
                    <span className="next-bill-date">
                      {getDaysLeft(user?.subscriptionExpiresAt) > 0 ? 'Următoarea facturare: ' : 'Expirat la: '}
                      <br />
                      <span style={{ display: 'inline-block', fontWeight: 600 }}>
                        {moment(user?.subscriptionExpiresAt).format('DD MMMM YYYY')}
                      </span>
                    </span>

                    {getDaysLeft(user?.subscriptionExpiresAt) < 90 && (
                      <Button
                        onClick={goToRenewSubscription}
                        text="Achită factura"
                        icon={faArrowRight}
                        style={{ marginTop: 4 }}
                      />
                    )}
                  </>
                ) : (
                  <Button
                    onClick={goToRenewSubscription}
                    text={IS_DESKTOP_OR_TABLET ? 'Achiziționează abonament' : 'Achiziționează'}
                    icon={faArrowRight}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {user?.recurringPayment && (
          <div className="section-wrapper">
            <label>Plată recurentă</label>
            <div className="recurring-payment-info">
              <div className="info-row">
                <span className="info-label">Status:</span>
                <span className={`info-value ${user.recurringPayment.isActive ? 'active' : 'inactive'}`}>
                  {user.recurringPayment.isActive ? 'Activă' : 'Dezactivată'}
                </span>
              </div>

              {!user.recurringPayment.isActive && user.recurringPayment.cancelledAt && (
                <div className="info-row">
                  <span className="info-label">Dezactivată la:</span>
                  <span className="info-value">{moment(user.recurringPayment.cancelledAt).format('DD MMMM YYYY')}</span>
                </div>
              )}

              {!user.recurringPayment.isActive && user.recurringPayment.cancelReason && (
                <div className="info-row">
                  <span className="info-label">Motiv:</span>
                  <span className="info-value">{user.recurringPayment.cancelReason}</span>
                </div>
              )}

              <div className="info-row">
                <span className="info-label">Interval plată:</span>
                <span className="info-value">
                  {moment.duration(user.recurringPayment.recurringInterval, 'days').asMonths() === 1
                    ? '1 lună'
                    : `${Math.round(moment.duration(user.recurringPayment.recurringInterval, 'days').asMonths())} luni`}
                </span>
              </div>

              {user.recurringPayment.recurringPaymentCardMask && (
                <div className="info-row">
                  <span className="info-label">Card:</span>
                  <span className="info-value">{user.recurringPayment.recurringPaymentCardMask}</span>
                </div>
              )}

              {user.recurringPayment.isActive && user.subscriptionExpiresAt && (
                <div className="info-row">
                  <span className="info-label">Următoarea plată:</span>
                  <span className="info-value">{moment(user.subscriptionExpiresAt).format('DD MMMM YYYY')}</span>
                </div>
              )}

              {user.recurringPayment.lastPaymentSuccessDate && (
                <div className="info-row">
                  <span className="info-label">Ultima plată:</span>
                  <span className="info-value">
                    {moment(user.recurringPayment.lastPaymentSuccessDate).format('DD MMMM YYYY')}
                  </span>
                </div>
              )}

              <div className="info-row">
                <span className="info-label">Total plăți automate:</span>
                <span className="info-value">{user.recurringPayment.totalRecurringPayments || 0}</span>
              </div>

              {Boolean(user.recurringPayment.failedPaymentAttempts) && (
                <div className="info-row">
                  <span className="info-label">Încercări eșuate:</span>
                  <span className="info-value warning">{user.recurringPayment.failedPaymentAttempts}</span>
                </div>
              )}

              {user.recurringPayment.isActive ? (
                <Button text="Dezactivează plata recurentă" onClick={cancelRecurringPayment} style={{ marginTop: 16 }} />
              ) : (
                <Button text="Reactivează plata recurentă" onClick={reactivateRecurringPayment} style={{ marginTop: 16 }} />
              )}
            </div>
          </div>
        )}

        {businessOrderList?.length > 0 && (
          <div className="orders-button-wrapper">
            <Button variant="as-text" text="Vezi istoricul plăților..." onClick={() => navigate(Screen.BusinessOrders)} />
          </div>
        )}

        {isPaying(user) && (
          <div className="section-wrapper">
            <label>Încetare abonament</label>
            <p>
              Dorești încetarea abonamentului și restituirea plății efectuate? Trimite-ne un email la contact@meniudigital.ro cu
              subiectul "Anulare abonament". <br />
              <br />
              Te rugăm să incluzi:
              <br />
              <br /> • username-ul contului tău
              <br /> • data plății și suma
              <br />• motivul anulării (opțional)
              <br />
              <br /> Vom confirma anularea și restituirea sumei în cel mai scurt timp.
            </p>
          </div>
        )}
      </div>

      <SubscriptionInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        onPurchase={() => {
          setIsInfoModalOpen(false)
          goToRenewSubscription()
        }}
      />
    </Wrapper>
  )
}
